import { WarningFilled } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { REQUEST_STATUS } from "../../utils/constants";
import useInitApi from "../apiHooks/useInitApi";
import useUsersApi from "../apiHooks/useUsersApi";
import { useAuthContext } from "../core/contexts/AuthContextProvider";
import { ROUTES } from "../core/navigation";
import { warningColor } from "../theme";
import "./loader.css";

const loaderClasses = ["loader-1", "loader-2", "loader-3", "circle-loader"];

const loadingTexts = [
  "Please wait while we prepare your site",
  "Brewing some fresh pixels",
  "Polishing the magic",
  "Almost there.. tightening a few screws",
  "Warming up the engines",
  "Sprinkling some final touches",
];

const AppLoader = () => {
  const [textIndex, setTextIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const [isError, setIsError] = useState(false);

  const {
    getAllPermissions,
    isPermissionError,
    getAllEntitledModules,
    isEntitledModulesError,
  } = useAuthContext();
  const isAppInitialized = useSelector(
    (state) => state.common.isAppInitialized,
  );

  const {
    common: { SET_APP_INIT },
  } = useDispatch();

  const navigate = useNavigate();

  const {
    common: { SET_LOGGED_USER },
  } = useDispatch();

  const {
    getCurrentUserApi: { get: fetchCurrentUser, error: userError },
  } = useUsersApi();

  const {
    checkHealthApi: { get: checkHealth, error: healthError },
    warmupDBApi: { get: warmupDB, error: dbError },
  } = useInitApi();

  const navigateToDashboard = useCallback(
    () => navigate("/" + ROUTES.dashboard),
    [navigate],
  );

  const handleAppInit = useCallback(async () => {
    try {
      const appHealth = await checkHealth();
      if (appHealth.data.status === REQUEST_STATUS.ok) {
        const db = await warmupDB();
        if (db.status === REQUEST_STATUS.success) {
          const userResponse = await fetchCurrentUser();
          if (userResponse.status === REQUEST_STATUS.success) {
            SET_LOGGED_USER(userResponse.data);

            await Promise.all([getAllPermissions(), getAllEntitledModules()]);

            SET_APP_INIT(true);

            navigateToDashboard();
          }
        }
      }
    } catch (err) {
      setIsError(true);
    }
  }, [
    SET_APP_INIT,
    SET_LOGGED_USER,
    checkHealth,
    fetchCurrentUser,
    getAllEntitledModules,
    getAllPermissions,
    navigateToDashboard,
    warmupDB,
  ]);

  const randomLoader = useMemo(
    () => loaderClasses[Math.floor(Math.random() * loaderClasses.length)],
    [],
  );

  useEffect(() => {
    if (isAppInitialized) {
      navigateToDashboard();
      return;
    }

    handleAppInit();

    const interval = setInterval(() => {
      setFade(false);

      setTimeout(() => {
        setTextIndex(Math.floor(Math.random() * loadingTexts.length));
        setFade(true);
      }, 300);
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      healthError ||
      dbError ||
      isPermissionError ||
      userError ||
      isEntitledModulesError
    ) {
      setIsError(true);
    }
  }, [
    healthError,
    dbError,
    isPermissionError,
    userError,
    isEntitledModulesError,
  ]);

  return (
    <div className="app-loader">
      {isError ? (
        <>
          <WarningFilled style={{ fontSize: 75, color: warningColor }} />
          <p className="loader-text">
            Could not connect to the server. Please contact support.
          </p>
        </>
      ) : (
        <>
          {randomLoader === "circle-loader" ? (
            <div className="circle-loader">
              {[...Array(9)].map((_, i) => (
                <div key={i} className={`circle circle${i + 1}`}></div>
              ))}
            </div>
          ) : (
            <div className={randomLoader}></div>
          )}

          <p className={`loader-text ${fade ? "fade-in" : "fade-out"}`}>
            {loadingTexts[textIndex]}
          </p>
        </>
      )}
    </div>
  );
};

export default AppLoader;
